import { Link } from 'react-router-dom';
import styles from './MarketingSuggestions.module.css';

type HashtagEntry = {
  tag: string;
  purpose: string;
};

type HashtagCategory = {
  name: string;
  description: string;
  tags: HashtagEntry[];
};

const suggestions: string[] = [
  'Lead with a specific pain point (scattered notes, forgotten names, lost contacts) before mentioning the product—problem-aware posts convert best.',
  'Always pair a hashtag set with a direct page link (e.g. /features, /consolidate, /for-networkers) so clicks land on relevant, high-intent pages.',
  'Mix 1 brand tag + 1 broad reach tag + 2 niche/audience tags per post for the best balance of discovery and relevance.',
  'Target one audience per post (seniors, networkers, wedding planners, coaches, church leaders) and use that audience\'s dedicated hashtags.',
  'Rotate the privacy/data-ownership angle for tech and security audiences—these tags reach a distinct, values-driven crowd.',
  'Reuse proven tweet copy from public/docs/tweets.txt as a starting point, then swap the hashtag block to test different audiences.',
  'Affiliate-focused posts should link to /affiliate-program, /affiliate-application, or /affiliate-pitch and use the affiliate hashtag set.',
  'Post life-event content (weddings, reunions, graduations) seasonally when those audiences are actively planning.',
  'Keep to 3-4 hashtags on X/LinkedIn and up to 8-10 on Instagram; over-tagging reduces reach on most platforms.',
  'Track which hashtag groups drive the most affiliate conversions and double down on the winning audiences.',
];

const categories: HashtagCategory[] = [
  {
    name: 'Brand & Product Core',
    description: 'General-purpose tags that describe what MPD is. Use for top-of-funnel awareness and broad reach.',
    tags: [
      { tag: '#CRM', purpose: 'Positions MPD in the broad CRM conversation.' },
      { tag: '#PersonalCRM', purpose: 'Core category tag—MPD as a personal (not sales) CRM.' },
      { tag: '#ProfessionalCRM', purpose: 'CRM angle aimed at professional users.' },
      { tag: '#NotSaleforce', purpose: 'Contrasts MPD with heavy enterprise sales CRMs.' },
      { tag: '#PeopleDatabase', purpose: 'Literal product description; reinforces the name.' },
      { tag: '#ContactManagement', purpose: 'Broad, high-volume category for contact organization.' },
      { tag: '#RelationshipManagement', purpose: 'Emphasizes managing relationships, not just contacts.' },
      { tag: '#RelationshipIntelligence', purpose: 'Premium framing—smart insight into relationships.' },
      { tag: '#DesktopApp', purpose: 'Highlights MPD as a native desktop application.' },
      { tag: '#MacApp', purpose: 'Targets Mac users searching for apps.' },
      { tag: '#Productivity', purpose: 'Broad reach into the productivity community.' },
      { tag: '#ProductivityApps', purpose: 'Discovery among productivity app seekers.' },
      { tag: '#ProductivityTools', purpose: 'Tool-focused productivity discovery.' },
      { tag: '#ProductivityTips', purpose: 'For tip-style productivity content.' },
      { tag: '#OrganizationTools', purpose: 'Reaches people looking to get organized.' },
      { tag: '#Organization', purpose: 'Broad organization/decluttering audience.' },
      { tag: '#GetOrganized', purpose: 'Action-oriented call for organization content.' },
      { tag: '#OrganizedLife', purpose: 'Lifestyle framing of being organized.' },
      { tag: '#FeatureTour', purpose: 'Tag for posts walking through MPD features.' },
      { tag: '#TryItFree', purpose: 'Encourages trial/download conversions.' },
      { tag: '#GetStarted', purpose: 'Onboarding/first-step call to action.' },
      { tag: '#EasySetup', purpose: 'Reassures on low friction to begin.' },
      { tag: '#EverydayLife', purpose: 'Positions MPD for everyday, non-business use.' },
      { tag: '#HumanRelationships', purpose: 'Human-centered framing of the product.' },
      { tag: '#HumanFirst', purpose: 'Values tag—people over data exploitation.' },
      { tag: '#PeopleFirst', purpose: 'Brand value tag emphasizing people.' },
      { tag: '#ContextMatters', purpose: 'Core message—store context, not just contacts.' },
      { tag: '#RememberMore', purpose: 'Benefit tag around memory augmentation.' },
      { tag: '#NeverForget', purpose: 'Benefit tag—never forget people or details.' },
      { tag: '#Simplicity', purpose: 'Emphasizes MPD\'s simple, focused design.' },
      { tag: '#SimpleTech', purpose: 'Approachable-tech angle (great for seniors).' },
      { tag: '#OnePlace', purpose: 'Consolidation benefit—everything in one place.' },
      { tag: '#OneSource', purpose: 'Single source of truth messaging.' },
    ],
  },
  {
    name: 'Privacy, Data Ownership & Ethics',
    description: 'Values-driven tags for privacy-conscious and anti-surveillance audiences. Link to /data-principles and /why.',
    tags: [
      { tag: '#PrivacyFirst', purpose: 'Flagship privacy positioning tag.' },
      { tag: '#PrivacyMatters', purpose: 'Broad privacy-advocacy reach.' },
      { tag: '#PrivacyByDesign', purpose: 'Signals privacy built into architecture.' },
      { tag: '#PrivacyLaw', purpose: 'For posts referencing GDPR/CCPA/regulation.' },
      { tag: '#PrivacyTools', purpose: 'Reaches people seeking privacy software.' },
      { tag: '#PrivacyAdvocate', purpose: 'Targets privacy advocates/influencers.' },
      { tag: '#PrivacyCommunity', purpose: 'Reaches the broader privacy community.' },
      { tag: '#DataOwnership', purpose: 'Core value—you own your relationship data.' },
      { tag: '#OwnYourData', purpose: 'Action-framed data ownership message.' },
      { tag: '#OwnYourNetwork', purpose: 'Own your relationships, not the platform.' },
      { tag: '#OwnYourContacts', purpose: 'Specific ownership angle for contacts.' },
      { tag: '#YourContacts', purpose: 'Emphasizes user ownership of contacts.' },
      { tag: '#YourDataYourWay', purpose: 'Flexibility + ownership messaging.' },
      { tag: '#DataFreedom', purpose: 'Freedom to move/own your data.' },
      { tag: '#DataRights', purpose: 'Frames data control as a right.' },
      { tag: '#DigitalRights', purpose: 'Broader digital-rights audience.' },
      { tag: '#DataPrivacy', purpose: 'High-volume privacy discovery tag.' },
      { tag: '#DataProtection', purpose: 'Security/compliance-flavored privacy tag.' },
      { tag: '#DataSovereignty', purpose: 'Control over where data lives.' },
      { tag: '#DataSafety', purpose: 'Reassurance that data is safe.' },
      { tag: '#DataExploitation', purpose: 'Contrast tag—what MPD refuses to do.' },
      { tag: '#AntiSurveillance', purpose: 'Positions against surveillance capitalism.' },
      { tag: '#Surveillance', purpose: 'For posts critiquing surveillance models.' },
      { tag: '#NoTracking', purpose: 'Highlights no tracking of users.' },
      { tag: '#NoAds', purpose: 'Ad-free product differentiator.' },
      { tag: '#AdTech', purpose: 'Contrast against ad-tech data mining.' },
      { tag: '#EthicalTech', purpose: 'Ethical-technology positioning.' },
      { tag: '#NoLockin', purpose: 'No vendor lock-in (spelling variant).' },
      { tag: '#NoLockIn', purpose: 'No vendor lock-in (primary spelling).' },
      { tag: '#PlatformIndependent', purpose: 'Not dependent on any platform.' },
      { tag: '#LocalFirst', purpose: 'Local-first, non-cloud data storage.' },
      { tag: '#NoCloud', purpose: 'Emphasizes no forced cloud storage.' },
      { tag: '#TotalControl', purpose: 'User has complete control of data.' },
      { tag: '#GDPR', purpose: 'Targets GDPR-aware/EU audience.' },
      { tag: '#CCPA', purpose: 'Targets California/US privacy-law audience.' },
      { tag: '#Compliance', purpose: 'Compliance-minded professional audience.' },
    ],
  },
  {
    name: 'Cybersecurity & Risk',
    description: 'Security-audience tags framing contact data as an attack surface. Link to /data-principles and /why.',
    tags: [
      { tag: '#CyberSecurity', purpose: 'Broad security-community reach.' },
      { tag: '#InfoSec', purpose: 'Information-security professional audience.' },
      { tag: '#DataBreach', purpose: 'For breach-related news hooks.' },
      { tag: '#CyberRisk', purpose: 'Frames scattered data as risk.' },
      { tag: '#CyberAwareness', purpose: 'Security-awareness content angle.' },
      { tag: '#CyberHygiene', purpose: 'Good data-handling habits framing.' },
      { tag: '#AttackSurface', purpose: 'Less exposed data = smaller attack surface.' },
      { tag: '#SocialEngineering', purpose: 'Ties contact data to social-engineering risk.' },
      { tag: '#Phishing', purpose: 'Relates harvested contacts to phishing.' },
      { tag: '#EmailSecurity', purpose: 'Email-threat framing.' },
      { tag: '#MobileSecurity', purpose: 'Mobile/contact-permission risk angle.' },
      { tag: '#AppSecurity', purpose: 'App-level data exposure angle.' },
      { tag: '#CloudSecurity', purpose: 'Risk of default cloud syncing.' },
      { tag: '#APIsecurity', purpose: 'API-exploitation/data-dump angle.' },
      { tag: '#ContactPermissions', purpose: 'Warns about apps copying your contacts.' },
      { tag: '#IdentityTheft', purpose: 'Ties relationship data to identity theft.' },
      { tag: '#ScamPrevention', purpose: 'Scam-avoidance angle (great for seniors).' },
      { tag: '#SecureYourData', purpose: 'Action CTA to secure data.' },
      { tag: '#ProtectYourContacts', purpose: 'Protect the people in your network.' },
      { tag: '#DigitalResilience', purpose: 'Durable, resilient data ownership.' },
      { tag: '#PlatformRisk', purpose: 'Risk of platform shutdown/lock-in.' },
    ],
  },
  {
    name: 'Networking & Professional',
    description: 'For professional networkers, BD pros, and consultants. Link to /for-networkers.',
    tags: [
      { tag: '#Networking', purpose: 'Core networking-audience tag.' },
      { tag: '#ProfessionalNetworking', purpose: 'Professional-focused networking.' },
      { tag: '#ProfessionalNetwork', purpose: 'Owning your professional network.' },
      { tag: '#NetworkingStrategy', purpose: 'Strategic networking content.' },
      { tag: '#NetworkingSystem', purpose: 'System-over-memory messaging.' },
      { tag: '#NetworkingTools', purpose: 'Tool discovery for networkers.' },
      { tag: '#NetworkingPro', purpose: 'Aimed at power networkers.' },
      { tag: '#NetworkingPrep', purpose: 'Showing up prepared to meetings.' },
      { tag: '#NetworkingROI', purpose: 'ROI-of-networking framing.' },
      { tag: '#NetworkingFail', purpose: 'Pain point—lost connections.' },
      { tag: '#SmartNetworking', purpose: 'Smarter, organized networking.' },
      { tag: '#EventNetworking', purpose: 'Networking at events/conferences.' },
      { tag: '#StrategicNetworking', purpose: 'Network as strategic asset.' },
      { tag: '#NetworkMapping', purpose: 'Relationship-mapping feature angle.' },
      { tag: '#ReferralNetwork', purpose: 'Referrals/introductions framing.' },
      { tag: '#BusinessReferrals', purpose: 'Referral-generation angle.' },
      { tag: '#BusinessDevelopment', purpose: 'BD professionals audience.' },
      { tag: '#BusinessGrowth', purpose: 'Growth-through-relationships angle.' },
      { tag: '#BusinessStrategy', purpose: 'Strategy-minded business audience.' },
      { tag: '#BusinessConnections', purpose: 'Building business connections.' },
      { tag: '#BusinessMeetings', purpose: 'Meeting-prep use case.' },
      { tag: '#BusinessOrganization', purpose: 'Organizing business contacts.' },
      { tag: '#RelationshipCapital', purpose: 'Network as capital/net worth.' },
      { tag: '#ProfessionalGrowth', purpose: 'Career/professional growth angle.' },
      { tag: '#ProfessionalEdge', purpose: 'Competitive edge from preparation.' },
      { tag: '#ProfessionalTools', purpose: 'Professional tool discovery.' },
      { tag: '#IndustryConnections', purpose: 'Tagging contacts by industry.' },
      { tag: '#FindPeople', purpose: 'Search-your-network benefit.' },
      { tag: '#FollowUp', purpose: 'Follow-up discipline messaging.' },
      { tag: '#LinkedIn', purpose: 'Contrast/compliment to LinkedIn.' },
      { tag: '#LinkedInTips', purpose: 'LinkedIn-adjacent tips audience.' },
      { tag: '#ConferenceLife', purpose: 'Conference-goer audience.' },
      { tag: '#ConferenceContacts', purpose: 'Capturing conference contacts.' },
      { tag: '#FirstImpressions', purpose: 'Making strong first impressions.' },
      { tag: '#PeopleSkills', purpose: 'Soft-skills/people-skills audience.' },
      { tag: '#ShowUp', purpose: 'Show up prepared and present.' },
      { tag: '#ShowUpBetter', purpose: 'Be better for the people you meet.' },
      { tag: '#BePresent', purpose: 'Presence and thoughtfulness angle.' },
    ],
  },
  {
    name: 'Groups, Communities & Organizers',
    description: 'For community organizers, group leaders, and volunteers. Link to /features-groups and /private-sharing-groups.',
    tags: [
      { tag: '#SmartGroups', purpose: 'Core groups-feature tag.' },
      { tag: '#GroupManagement', purpose: 'Managing groups of people.' },
      { tag: '#ScopedSharing', purpose: 'Share info scoped to the right people.' },
      { tag: '#SecureSharing', purpose: 'Secure/private sharing angle.' },
      { tag: '#IntentionalSharing', purpose: 'Deliberate, intentional sharing.' },
      { tag: '#ShareByIntent', purpose: 'Private by default, share by intent.' },
      { tag: '#Teamwork', purpose: 'Team coordination use case.' },
      { tag: '#TeamManagement', purpose: 'Managing team rosters/contacts.' },
      { tag: '#Workflow', purpose: 'Real-world group workflows.' },
      { tag: '#RealLifeUse', purpose: 'Practical, real-life group examples.' },
      { tag: '#CommunityBuilding', purpose: 'Building and maintaining communities.' },
      { tag: '#CommunityLife', purpose: 'Community lifestyle audience.' },
      { tag: '#CommunityMatters', purpose: 'Value of community relationships.' },
      { tag: '#CommunityEvents', purpose: 'Community-event organizing.' },
      { tag: '#CommunityService', purpose: 'Service/volunteer community angle.' },
      { tag: '#CommunityOrganizer', purpose: 'Targets community organizers.' },
      { tag: '#CommunityLeader', purpose: 'Targets community leaders.' },
      { tag: '#Organizers', purpose: 'Broad organizer audience.' },
      { tag: '#Leadership', purpose: 'Leadership/organizing audience.' },
      { tag: '#PeopleOps', purpose: 'People-operations framing.' },
      { tag: '#Nonprofit', purpose: 'Nonprofit organizations (variant).' },
      { tag: '#NonProfit', purpose: 'Nonprofit organizations (variant).' },
      { tag: '#NonprofitTools', purpose: 'Tools for nonprofits.' },
      { tag: '#Alumni', purpose: 'Alumni-network organizers.' },
      { tag: '#DonorManagement', purpose: 'Managing donor contacts.' },
      { tag: '#VolunteerCoordination', purpose: 'Coordinating volunteers.' },
      { tag: '#VolunteerLife', purpose: 'Volunteer audience lifestyle.' },
      { tag: '#Volunteers', purpose: 'General volunteer audience.' },
      { tag: '#ServeOthers', purpose: 'Service-minded messaging.' },
      { tag: '#Fundraiser', purpose: 'Fundraising-event organizing.' },
      { tag: '#HOA', purpose: 'Homeowners-association leaders.' },
      { tag: '#NeighborhoodWatch', purpose: 'Neighborhood-watch coordinators.' },
      { tag: '#Veterans', purpose: 'Veterans-group organizers.' },
      { tag: '#VeteransCommunity', purpose: 'Veterans community audience.' },
      { tag: '#MilitaryFamily', purpose: 'Military-family networks.' },
      { tag: '#BuddyCheck', purpose: 'Veteran buddy check-in use case.' },
    ],
  },
  {
    name: 'Faith & Ministry',
    description: 'For church leaders, small groups, and ministries. Link to /private-sharing-groups.',
    tags: [
      { tag: '#ChurchLife', purpose: 'Broad church-audience tag.' },
      { tag: '#ChurchVolunteers', purpose: 'Coordinating church volunteers.' },
      { tag: '#Ministry', purpose: 'General ministry audience.' },
      { tag: '#YouthMinistry', purpose: 'Youth-ministry leaders.' },
      { tag: '#FaithCommunity', purpose: 'Faith-community organizers.' },
      { tag: '#SmallGroups', purpose: 'Small-group leaders.' },
      { tag: '#BibleStudy', purpose: 'Bible-study organizers.' },
      { tag: '#PrayerRequest', purpose: 'Sharing prayer requests privately.' },
      { tag: '#DiscipleshipTools', purpose: 'Discipleship/ministry tools.' },
      { tag: '#YouthGroup', purpose: 'Youth-group leaders.' },
    ],
  },
  {
    name: 'Sports & Team Coordination',
    description: 'For coaches, team parents, and league organizers. Link to /features-groups and /private-sharing-groups.',
    tags: [
      { tag: '#YouthSports', purpose: 'Youth-sports parents/coaches.' },
      { tag: '#SportsParents', purpose: 'Sports-parent audience.' },
      { tag: '#CoachLife', purpose: 'Coaches audience.' },
      { tag: '#SoccerMom', purpose: 'Soccer-parent audience.' },
      { tag: '#SoccerDad', purpose: 'Soccer-parent audience.' },
      { tag: '#SwimTeam', purpose: 'Swim-team coordinators.' },
      { tag: '#CarpoolLife', purpose: 'Carpool-coordination use case.' },
      { tag: '#FantasyFootball', purpose: 'Fantasy-league organizers.' },
      { tag: '#NFL', purpose: 'Football-fan reach.' },
      { tag: '#FFball', purpose: 'Fantasy-football niche tag.' },
      { tag: '#LeagueLife', purpose: 'League-participant audience.' },
      { tag: '#GameDay', purpose: 'Game-day coordination content.' },
      { tag: '#Basketball', purpose: 'Basketball-team audience.' },
      { tag: '#RecLeague', purpose: 'Recreational-league organizers.' },
      { tag: '#PickupHoops', purpose: 'Pickup-basketball niche.' },
      { tag: '#HoopLife', purpose: 'Basketball-lifestyle niche.' },
      { tag: '#Softball', purpose: 'Softball-team managers.' },
      { tag: '#DiamondLife', purpose: 'Baseball/softball niche.' },
      { tag: '#RunningCommunity', purpose: 'Running-club organizers.' },
      { tag: '#RunClub', purpose: 'Run-club audience.' },
      { tag: '#Runners', purpose: 'Runner audience.' },
      { tag: '#MarathonTraining', purpose: 'Training-group coordination.' },
      { tag: '#FitnessMotivation', purpose: 'Fitness-community reach.' },
    ],
  },
  {
    name: 'Seniors & Caregiving',
    description: 'For older adults, caregivers, and family health coordination. Link to /features and /download.',
    tags: [
      { tag: '#Seniors', purpose: 'Core senior audience.' },
      { tag: '#SeniorLiving', purpose: 'Senior-living lifestyle audience.' },
      { tag: '#SeniorLife', purpose: 'Everyday senior life.' },
      { tag: '#SeniorTech', purpose: 'Tech-for-seniors angle.' },
      { tag: '#SeniorHealth', purpose: 'Senior-health/contacts angle.' },
      { tag: '#SeniorCare', purpose: 'Senior-care audience.' },
      { tag: '#SeniorSafety', purpose: 'Safety/scam-prevention for seniors.' },
      { tag: '#SeniorSocial', purpose: 'Seniors\' social-life organizing.' },
      { tag: '#SeniorCenter', purpose: 'Senior-center coordinators.' },
      { tag: '#SeniorWisdom', purpose: 'Wisdom/legacy framing for seniors.' },
      { tag: '#ActiveAging', purpose: 'Active-aging community.' },
      { tag: '#ActiveSeniors', purpose: 'Active-senior audience.' },
      { tag: '#AgingWell', purpose: 'Aging-well lifestyle.' },
      { tag: '#Retirement', purpose: 'Retirement audience.' },
      { tag: '#RetirementLife', purpose: 'Retirement-lifestyle content.' },
      { tag: '#RetirementCommunity', purpose: 'Retirement-community organizers.' },
      { tag: '#Downsizing', purpose: 'Downsizing/moving seniors.' },
      { tag: '#Snowbird', purpose: 'Snowbirds with dual communities.' },
      { tag: '#DualCommunity', purpose: 'Managing two communities.' },
      { tag: '#Caregiver', purpose: 'Caregiver audience.' },
      { tag: '#CaregiverLife', purpose: 'Caregiving-lifestyle content.' },
      { tag: '#FamilyCaregiver', purpose: 'Family-caregiver audience.' },
      { tag: '#ElderCare', purpose: 'Elder-care coordination.' },
      { tag: '#HealthcareContacts', purpose: 'Organizing care-team contacts.' },
      { tag: '#MedicalContacts', purpose: 'Storing medical contacts.' },
      { tag: '#MedicationManagement', purpose: 'Managing medication-related contacts.' },
      { tag: '#DoctorAppointments', purpose: 'Tracking doctors/appointments.' },
      { tag: '#Grief', purpose: 'Grief/loss-sensitive content.' },
      { tag: '#Widowed', purpose: 'Widowed audience preserving relationships.' },
      { tag: '#PreserveMemories', purpose: 'Preserving relationship memories.' },
      { tag: '#MemoryHelp', purpose: 'Memory-assistance benefit.' },
    ],
  },
  {
    name: 'Family, Parenting & Grandparents',
    description: 'For parents, grandparents, and family organizers. Link to /features and /features-groups.',
    tags: [
      { tag: '#FamilyFirst', purpose: 'Family-centered messaging.' },
      { tag: '#FamilyTree', purpose: 'Family-tree/reunion angle.' },
      { tag: '#FamilyReunion', purpose: 'Reunion organizers.' },
      { tag: '#FamilyOrganizer', purpose: 'The family organizer persona.' },
      { tag: '#FamilyPlanning', purpose: 'Family logistics/planning.' },
      { tag: '#FamilyCrisis', purpose: 'Emergency family coordination.' },
      { tag: '#BlendedFamily', purpose: 'Blended-family organization.' },
      { tag: '#ModernFamily', purpose: 'Modern-family audience.' },
      { tag: '#ReunionPlanning', purpose: 'Reunion-planning content.' },
      { tag: '#Grandparents', purpose: 'Grandparent audience.' },
      { tag: '#GrandparentLife', purpose: 'Grandparent-lifestyle content.' },
      { tag: '#GrandparentsOfTwitter', purpose: 'Grandparent community tag.' },
      { tag: '#ParentLife', purpose: 'Parent audience.' },
      { tag: '#OrganizedParent', purpose: 'Organized-parent persona.' },
      { tag: '#KidsNetwork', purpose: 'Tracking kids\' contacts/coaches.' },
      { tag: '#SchoolContacts', purpose: 'School-contact organization.' },
      { tag: '#SchoolDirectory', purpose: 'Digitizing class directories.' },
      { tag: '#ClassList', purpose: 'Class-list management.' },
      { tag: '#KidSafety', purpose: 'Emergency-contact/kid-safety angle.' },
      { tag: '#EmergencyContacts', purpose: 'Storing emergency contacts.' },
      { tag: '#EmergencyPrep', purpose: 'Emergency-preparedness angle.' },
      { tag: '#BeReady', purpose: 'Preparedness call to action.' },
    ],
  },
  {
    name: 'Life Events & Celebrations',
    description: 'Seasonal, high-intent event planning tags. Link to /features-groups and /features.',
    tags: [
      { tag: '#EventPlanning', purpose: 'Core event-planning tag.' },
      { tag: '#EventManagement', purpose: 'Managing event contacts.' },
      { tag: '#EventOrganizer', purpose: 'Event-organizer persona.' },
      { tag: '#EventPlanner', purpose: 'Professional event planners.' },
      { tag: '#EventProfs', purpose: 'Event-professionals community.' },
      { tag: '#EventContacts', purpose: 'Capturing contacts from events.' },
      { tag: '#ProfessionalEvents', purpose: 'Professional/corporate events.' },
      { tag: '#ConferencePlanning', purpose: 'Conference organizers.' },
      { tag: '#SpeakerManagement', purpose: 'Managing speakers/sponsors.' },
      { tag: '#VendorManagement', purpose: 'Managing event vendors.' },
      { tag: '#GuestList', purpose: 'Guest-list management.' },
      { tag: '#RSVP', purpose: 'RSVP-tracking use case.' },
      { tag: '#PartyPlanning', purpose: 'Party-planning audience.' },
      { tag: '#CharityGala', purpose: 'Gala/fundraiser organizers.' },
      { tag: '#GraduationParty', purpose: 'Graduation-party planners.' },
      { tag: '#ClassOf2026', purpose: 'Graduation-season timely tag.' },
      { tag: '#MilestoneCelebration', purpose: 'Milestone-event content.' },
      { tag: '#HostWithConfidence', purpose: 'Confident-hosting benefit.' },
      { tag: '#HappyHost', purpose: 'Stress-free hosting angle.' },
      { tag: '#PlanWithConfidence', purpose: 'Confident-planning benefit.' },
      { tag: '#WeddingPlanning', purpose: 'Core wedding-planning tag.' },
      { tag: '#WeddingPlanner', purpose: 'Professional wedding planners.' },
      { tag: '#WeddingOrganization', purpose: 'Organizing wedding details.' },
      { tag: '#WeddingVendors', purpose: 'Managing wedding vendors.' },
      { tag: '#WeddingGuestList', purpose: 'Wedding guest-list management.' },
      { tag: '#WeddingEvents', purpose: 'Multi-event wedding weekends.' },
      { tag: '#WeddingWeekend', purpose: 'Wedding-weekend logistics.' },
      { tag: '#WeddingParty', purpose: 'Wedding-party coordination.' },
      { tag: '#WeddingGifts', purpose: 'Tracking gifts for thank-yous.' },
      { tag: '#WeddingEtiquette', purpose: 'Etiquette/thank-you angle.' },
      { tag: '#WeddingMemories', purpose: 'Post-wedding relationship keeping.' },
      { tag: '#WeddingTravel', purpose: 'Destination-wedding travel.' },
      { tag: '#TravelWedding', purpose: 'Destination-wedding audience.' },
      { tag: '#DestinationWedding', purpose: 'Destination-wedding planners.' },
      { tag: '#BrideToBe', purpose: 'Bride audience.' },
      { tag: '#BridalLife', purpose: 'Bridal-lifestyle content.' },
      { tag: '#BridalPlanning', purpose: 'Bridal-planning content.' },
      { tag: '#BridalShower', purpose: 'Bridal-shower events.' },
      { tag: '#BridalParty', purpose: 'Bridal-party coordination.' },
      { tag: '#Bridesmaids', purpose: 'Bridesmaid-group coordination.' },
      { tag: '#Groomsmen', purpose: 'Groomsmen-group coordination.' },
      { tag: '#RehearsalDinner', purpose: 'Rehearsal-dinner guest lists.' },
      { tag: '#DIYWedding', purpose: 'DIY-wedding planners.' },
      { tag: '#BudgetWedding', purpose: 'Budget-conscious couples.' },
      { tag: '#Newlyweds', purpose: 'Post-wedding newlyweds.' },
      { tag: '#PostWedding', purpose: 'Post-wedding follow-up.' },
      { tag: '#HolidayCards', purpose: 'Holiday-card address management.' },
      { tag: '#ChristmasCards', purpose: 'Christmas-card season.' },
      { tag: '#AnnualChaos', purpose: 'Annual holiday-card pain point.' },
      { tag: '#AddressHunt', purpose: 'Pain point—hunting for addresses.' },
      { tag: '#ThankYouCards', purpose: 'Thank-you-card personalization.' },
    ],
  },
  {
    name: 'Consolidation & Pain Points',
    description: 'Problem-aware tags that name the frustration MPD solves. Link to /consolidate and /why.',
    tags: [
      { tag: '#ContactChaos', purpose: 'Names the scattered-contacts problem.' },
      { tag: '#ContactCleanup', purpose: 'Cleaning up messy contacts.' },
      { tag: '#ContactFragmentation', purpose: 'Fragmented-contacts pain point.' },
      { tag: '#ContactList', purpose: 'Basic contact-list pain framing.' },
      { tag: '#ContactBackup', purpose: 'Contact-backup/data-loss angle.' },
      { tag: '#ContactsApp', purpose: 'Contrast with basic contacts apps.' },
      { tag: '#DigitalDeclutter', purpose: 'Digital-decluttering audience.' },
      { tag: '#DigitalMess', purpose: 'The digital-mess pain point.' },
      { tag: '#DataSilos', purpose: 'Data trapped in silos.' },
      { tag: '#DataSprawl', purpose: 'Sprawling, scattered data.' },
      { tag: '#ConsolidateData', purpose: 'Consolidation CTA.' },
      { tag: '#InformationScatter', purpose: 'Scattered-information pain.' },
      { tag: '#ScatteredInfo', purpose: 'Scattered-info pain point.' },
      { tag: '#TakeControl', purpose: 'Take-control CTA.' },
      { tag: '#NotesTrap', purpose: 'Notes-scattered-everywhere pain.' },
      { tag: '#NoteFilesChaos', purpose: 'Chaotic note-files pain.' },
      { tag: '#AppleNotes', purpose: 'Contrast with Apple Notes.' },
      { tag: '#GoogleContacts', purpose: 'Contrast/import with Google Contacts.' },
      { tag: '#Spreadsheets', purpose: 'Contrast with spreadsheet tracking.' },
      { tag: '#ExcelHell', purpose: 'Spreadsheet-pain framing.' },
      { tag: '#SlackOverload', purpose: 'Data lost in chat apps.' },
      { tag: '#ChatAppChaos', purpose: 'Chat-app data-scatter pain.' },
      { tag: '#Limitations', purpose: 'Limits of basic contact apps.' },
      { tag: '#UpgradeYourTools', purpose: 'Upgrade-your-tooling CTA.' },
      { tag: '#Awkward', purpose: 'Forgot-a-name awkwardness hook.' },
      { tag: '#ForgotWho', purpose: 'Forgot-who-someone-is pain.' },
      { tag: '#NameBlank', purpose: 'Blanking-on-a-name pain.' },
      { tag: '#WhoIsWho', purpose: 'Distinguishing similar contacts.' },
      { tag: '#DuplicateNames', purpose: 'Multiple people, same name pain.' },
      { tag: '#WhoDisReally', purpose: '"New number who dis" pain hook.' },
      { tag: '#NewNumber', purpose: 'Lost-number pain point.' },
      { tag: '#PhoneProblems', purpose: 'Phone-contact limitations.' },
      { tag: '#PhoneSwitch', purpose: 'Data loss on phone switch.' },
      { tag: '#DataLoss', purpose: 'Data-loss pain point.' },
      { tag: '#LostContact', purpose: 'Losing a single contact.' },
      { tag: '#LostContacts', purpose: 'Losing multiple contacts.' },
      { tag: '#MissedConnections', purpose: 'Connections that slipped away.' },
      { tag: '#BrokenPromises', purpose: 'Forgotten follow-through pain.' },
      { tag: '#MovingDay', purpose: 'Relocation-contact pain.' },
      { tag: '#StartFresh', purpose: 'Fresh-start CTA when relocating.' },
      { tag: '#Referrals', purpose: 'Tracking who referred whom.' },
      { tag: '#RelationshipDetails', purpose: 'Remembering relationship details.' },
      { tag: '#NeighborLife', purpose: 'Neighbor-relationship examples.' },
      { tag: '#PaperPile', purpose: 'Paper-clutter pain point.' },
      { tag: '#CareerChange', purpose: 'Losing work contacts on job change.' },
      { tag: '#JobTransition', purpose: 'Job-transition contact loss.' },
      { tag: '#NeverAgain', purpose: 'Never-again emotional hook.' },
    ],
  },
  {
    name: 'Data Portability & Import/Export',
    description: 'For the no-lock-in, open-format, own-your-data message. Link to /features and /data-principles.',
    tags: [
      { tag: '#ImportExport', purpose: 'Core import/export capability.' },
      { tag: '#DataPortability', purpose: 'Move data anywhere, anytime.' },
      { tag: '#DataMigration', purpose: 'Migrating data between tools.' },
      { tag: '#FullExport', purpose: 'Complete-export benefit.' },
      { tag: '#OneClickExport', purpose: 'One-click-export benefit.' },
      { tag: '#NoDataLoss', purpose: 'No data lost on export.' },
      { tag: '#OpenFormats', purpose: 'CSV/vCard open-format support.' },
      { tag: '#OpenData', purpose: 'Open-data philosophy.' },
      { tag: '#VendorLockIn', purpose: 'Contrast against lock-in.' },
      { tag: '#DataTrap', purpose: 'Import-but-can\'t-leave pain.' },
      { tag: '#SwitchingCosts', purpose: 'Lowering switching costs.' },
      { tag: '#FairSoftware', purpose: 'Fair, user-respecting software.' },
      { tag: '#RespectUsers', purpose: 'User-respect messaging.' },
      { tag: '#UserRespect', purpose: 'User-respect messaging (variant).' },
      { tag: '#EarnedLoyalty', purpose: 'Loyalty earned, not forced.' },
      { tag: '#DigitalFreedom', purpose: 'Freedom-to-leave framing.' },
      { tag: '#DurableData', purpose: 'Durable, long-lived data.' },
      { tag: '#Backup', purpose: 'Backup-your-database benefit.' },
    ],
  },
  {
    name: 'Identity & MPD ID',
    description: 'For the Personal ID / MPD ID and QR sharing story. Link to /id.',
    tags: [
      { tag: '#DigitalIdentity', purpose: 'Core digital-identity tag.' },
      { tag: '#PortableIdentity', purpose: 'Portable, user-owned identity.' },
      { tag: '#MultipleIdentities', purpose: 'Multiple curated public cards.' },
      { tag: '#IdentityStack', purpose: 'Building a public-identity stack.' },
      { tag: '#SelfSovereign', purpose: 'Self-sovereign identity audience.' },
      { tag: '#Decentralized', purpose: 'Decentralized-identity audience.' },
      { tag: '#Web3Adjacent', purpose: 'Reaches Web3-curious crowd.' },
      { tag: '#PersonalBrand', purpose: 'Personal-branding angle.' },
      { tag: '#QRCode', purpose: 'QR-card sharing feature.' },
      { tag: '#ShareSmart', purpose: 'Smart, controlled sharing.' },
      { tag: '#BusinessCard', purpose: 'Replace-the-business-card angle.' },
      { tag: '#ModernNetworking', purpose: 'Modern QR-based networking.' },
      { tag: '#NoAppNeeded', purpose: 'No app required to receive your ID.' },
      { tag: '#NeverOutdated', purpose: 'Always-current shared info.' },
      { tag: '#AlwaysCurrent', purpose: 'Info stays up to date.' },
    ],
  },
  {
    name: 'Affiliate Program',
    description: 'For recruiting affiliates and resellers. Link to /affiliate-program, /affiliate-application, /affiliate-pitch.',
    tags: [
      { tag: '#AffiliateMarketing', purpose: 'Core affiliate-recruitment tag.' },
      { tag: '#AffiliateProgram', purpose: 'Program-specific reach.' },
      { tag: '#AffiliateIncome', purpose: 'Income-focused affiliate angle.' },
      { tag: '#AffiliateRevenue', purpose: 'Revenue-focused affiliate angle.' },
      { tag: '#AffiliateReseller', purpose: 'Reseller-focused recruitment.' },
      { tag: '#RealCommissions', purpose: 'Emphasizes real commission payouts.' },
      { tag: '#PassiveIncome', purpose: 'Passive-income seekers.' },
      { tag: '#PassiveRevenue', purpose: 'Passive-revenue framing.' },
      { tag: '#PassiveCommissions', purpose: 'Ongoing commission framing.' },
      { tag: '#SideHustle', purpose: 'Side-hustle audience.' },
      { tag: '#SimpleIncome', purpose: 'Simple, low-effort income.' },
      { tag: '#EasyIncome', purpose: 'Easy-income framing.' },
      { tag: '#EarnOnline', purpose: 'Earn-online audience.' },
      { tag: '#MakeMoneyOnline', purpose: 'Broad make-money-online reach.' },
      { tag: '#EarnFromHome', purpose: 'Work/earn-from-home audience.' },
      { tag: '#WorkFromAnywhere', purpose: 'Location-independent earners.' },
      { tag: '#EarnWhileYouHelp', purpose: 'Earn-by-helping framing.' },
      { tag: '#EarnWithPurpose', purpose: 'Purpose-driven earning.' },
      { tag: '#NoInventory', purpose: 'No inventory/shipping needed.' },
      { tag: '#NoStringsAttached', purpose: 'Simple terms messaging.' },
      { tag: '#ReferralProgram', purpose: 'Referral-program angle.' },
      { tag: '#WordOfMouth', purpose: 'Word-of-mouth earning.' },
      { tag: '#TellAFriend', purpose: 'Tell-a-friend referral CTA.' },
      { tag: '#ContentCreator', purpose: 'Recruiting content creators.' },
      { tag: '#ContentMarketing', purpose: 'Content-marketing affiliates.' },
      { tag: '#CreatorEconomy', purpose: 'Creator-economy audience.' },
      { tag: '#MonetizeYourContent', purpose: 'Content-monetization angle.' },
      { tag: '#MonetizeYourPlatform', purpose: 'Platform-monetization angle.' },
      { tag: '#EverGreenContent', purpose: 'Evergreen affiliate content.' },
      { tag: '#Blogger', purpose: 'Blogger affiliates.' },
      { tag: '#Podcaster', purpose: 'Podcaster affiliates.' },
      { tag: '#YouTuber', purpose: 'YouTuber affiliates.' },
      { tag: '#Influencer', purpose: 'Influencer affiliates.' },
      { tag: '#TechInfluencer', purpose: 'Tech-influencer affiliates.' },
      { tag: '#NewsletterWriter', purpose: 'Newsletter-writer affiliates.' },
      { tag: '#NewsletterMonetization', purpose: 'Newsletter-monetization angle.' },
      { tag: '#ProductivityNewsletter', purpose: 'Productivity-newsletter niche.' },
      { tag: '#TechReview', purpose: 'Tech-reviewer affiliates.' },
      { tag: '#HonestReview', purpose: 'Honest-review affiliate angle.' },
      { tag: '#TechTools', purpose: 'Tech-tool review audience.' },
      { tag: '#UniqueProduct', purpose: 'Unique-product content angle.' },
      { tag: '#CoachingBusiness', purpose: 'Coach affiliates.' },
      { tag: '#ConsultantLife', purpose: 'Consultant affiliates.' },
      { tag: '#Consulting', purpose: 'Consulting audience affiliates.' },
      { tag: '#ResourcePage', purpose: 'Add MPD to resource pages.' },
      { tag: '#HelpPeople', purpose: 'Help-people-and-earn framing.' },
      { tag: '#HonestMarketing', purpose: 'Honest-marketing affiliate angle.' },
      { tag: '#EthicalAffiliate', purpose: 'Ethical-affiliate positioning.' },
      { tag: '#AlignedValues', purpose: 'Values-aligned recommendations.' },
      { tag: '#TrustYourAudience', purpose: 'Recommend what you trust.' },
      { tag: '#TrustYourFollowers', purpose: 'Followers-first recommendations.' },
      { tag: '#MissionDriven', purpose: 'Mission-driven affiliate angle.' },
    ],
  },
  {
    name: 'Emotional Benefits & Well-being',
    description: 'For the feel-good, relationship-joy angle. Link to /features, /download, and /why.',
    tags: [
      { tag: '#PeaceOfMind', purpose: 'Calm-and-organized benefit.' },
      { tag: '#NoMoreStress', purpose: 'Stress-relief benefit.' },
      { tag: '#StressFree', purpose: 'Stress-free hosting/organizing.' },
      { tag: '#HappinessBoost', purpose: 'Happiness-benefit framing.' },
      { tag: '#Happiness', purpose: 'Broad happiness/well-being reach.' },
      { tag: '#FeelGood', purpose: 'Feel-good emotional hook.' },
      { tag: '#FeelGreat', purpose: 'Confidence/feel-great hook.' },
      { tag: '#DailyJoy', purpose: 'Everyday-satisfaction framing.' },
      { tag: '#SpreadJoy', purpose: 'Making others feel valued.' },
      { tag: '#RelationshipJoy', purpose: 'Joy from better relationships.' },
      { tag: '#JoyfulReunions', purpose: 'Reconnecting-with-friends joy.' },
      { tag: '#Reconnect', purpose: 'Reconnecting-with-people angle.' },
      { tag: '#OldFriends', purpose: 'Old-friends reconnection.' },
      { tag: '#Nostalgia', purpose: 'Nostalgic-reconnection hook.' },
      { tag: '#Thoughtful', purpose: 'Being-thoughtful benefit.' },
      { tag: '#Thoughtfulness', purpose: 'Thoughtfulness toward others.' },
      { tag: '#MakeOthersFeelValued', purpose: 'Make people feel valued.' },
      { tag: '#EmotionalIntelligence', purpose: 'EQ/relationship-skill angle.' },
      { tag: '#EmotionalWellbeing', purpose: 'Well-being benefit.' },
      { tag: '#MentalLoad', purpose: 'Reducing mental load.' },
      { tag: '#NoMoreGuilt', purpose: 'No-guilt-over-forgetting benefit.' },
      { tag: '#SocialConfidence', purpose: 'Confidence in social settings.' },
      { tag: '#NoMoreAwkward', purpose: 'End awkward name-blanking.' },
      { tag: '#Satisfaction', purpose: 'Satisfaction-of-being-organized.' },
      { tag: '#InControl', purpose: 'Feeling in control of your network.' },
      { tag: '#BetterFriend', purpose: 'Become a better friend.' },
      { tag: '#DeepConnections', purpose: 'Deeper-relationships benefit.' },
      { tag: '#MeaningfulConnections', purpose: 'Meaningful-connection framing.' },
      { tag: '#HumanConnections', purpose: 'Human-connection framing.' },
      { tag: '#RelationshipQuality', purpose: 'Higher-quality relationships.' },
      { tag: '#RelationshipGoals', purpose: 'Aspirational relationship framing.' },
      { tag: '#RelationshipBuilding', purpose: 'Building relationships over time.' },
      { tag: '#RelationshipContext', purpose: 'Context that deepens relationships.' },
      { tag: '#StayConnected', purpose: 'Staying-connected benefit.' },
      { tag: '#StayInTouch', purpose: 'Keeping-in-touch benefit.' },
      { tag: '#Friendships', purpose: 'Friendship-maintenance angle.' },
      { tag: '#Relationships', purpose: 'Broad relationships reach.' },
      { tag: '#Memories', purpose: 'Preserving-memories angle.' },
      { tag: '#LifeStory', purpose: 'People as part of your life story.' },
      { tag: '#LifeWellLived', purpose: 'Lifetime-of-relationships framing.' },
      { tag: '#LifetimeOfRelationships', purpose: 'A lifetime of relationships.' },
      { tag: '#PeacefulMind', purpose: 'Calm, organized state of mind.' },
    ],
  },
  {
    name: 'People-Beyond-Contacts & Misc',
    description: 'For the "store anyone, even without contact info" message plus remaining brand/story tags.',
    tags: [
      { tag: '#BeyondContacts', purpose: 'More than a contacts app.' },
      { tag: '#BeyondPhoneContacts', purpose: 'Beyond phone contacts.' },
      { tag: '#BeyondPhoneNumbers', purpose: 'People are more than numbers.' },
      { tag: '#PeopleNotNumbers', purpose: 'People-not-numbers messaging.' },
      { tag: '#PeopleContext', purpose: 'Context attached to each person.' },
      { tag: '#PeopleYouKnow', purpose: 'Everyone you know has a place.' },
      { tag: '#PeopleYouMeet', purpose: 'Capture people you meet.' },
      { tag: '#PeopleFromYourPast', purpose: 'Keep people from your past.' },
      { tag: '#RealContacts', purpose: 'Real people, not just data fields.' },
      { tag: '#RealRelationships', purpose: 'Real, everyday relationships.' },
      { tag: '#EveryConnection', purpose: 'Every connection counts.' },
      { tag: '#EveryoneCounts', purpose: 'Everyone deserves a place.' },
      { tag: '#RememberPeople', purpose: 'Remember the people who matter.' },
      { tag: '#NeverForgotten', purpose: 'No one gets forgotten.' },
      { tag: '#FlexibleData', purpose: 'Store whatever info you have.' },
      { tag: '#NoFieldRequired', purpose: 'No required fields to add someone.' },
      { tag: '#SmartContacts', purpose: 'Smarter contact storage.' },
      { tag: '#PartialInfo', purpose: 'Store people with partial info.' },
      { tag: '#GlobalCommunity', purpose: 'Culturally inclusive reach.' },
      { tag: '#CulturalContext', purpose: 'Respects cultural relationship norms.' },
      { tag: '#InclusiveDesign', purpose: 'Inclusive product-design angle.' },
      { tag: '#ModernNetworkingTag', purpose: 'Modern networking (story angle).' },
      { tag: '#Vision', purpose: 'Ties to the /vision story.' },
      { tag: '#LongTermThinking', purpose: 'Built-to-last framing.' },
      { tag: '#BuildInPublic', purpose: 'Build-in-public founder audience.' },
      { tag: '#ShipIt', purpose: 'Shipping/release-notes angle.' },
      { tag: '#ProductUpdates', purpose: 'Product-update announcements.' },
      { tag: '#Changelog', purpose: 'Release-notes/changelog posts.' },
      { tag: '#Transparency', purpose: 'Transparent-progress framing.' },
      { tag: '#TransparentBusiness', purpose: 'Open business-model framing.' },
      { tag: '#AlwaysImproving', purpose: 'Continuous-improvement message.' },
      { tag: '#Startup', purpose: 'Startup/founder audience.' },
      { tag: '#SaaS', purpose: 'SaaS/business-model audience.' },
      { tag: '#IndieFounder', purpose: 'Indie-founder community.' },
      { tag: '#Entrepreneurship', purpose: 'Entrepreneur audience.' },
      { tag: '#OneTimePurchase', purpose: 'One-time-purchase differentiator.' },
      { tag: '#NoSubscription', purpose: 'No-subscription differentiator.' },
      { tag: '#PayOnce', purpose: 'Pay-once pricing message.' },
      { tag: '#FairPricing', purpose: 'Fair-pricing message.' },
      { tag: '#SoftwareDeals', purpose: 'Software-deal seekers.' },
      { tag: '#NoBloatware', purpose: 'No-bloat, focused product.' },
      { tag: '#SmartSpending', purpose: 'Smart-spending/value framing.' },
      { tag: '#BusinessInvestment', purpose: 'Tool as an investment (ROI).' },
      { tag: '#NetworkingROItag', purpose: 'ROI framing for networkers.' },
    ],
  },
];

export default function MarketingSuggestions() {
  const totalTags = categories.reduce((sum, c) => sum + c.tags.length, 0);

  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <h1 className={styles.title}>Marketing Suggestions</h1>
      <p className={styles.subtitle}>
        A reference for affiliates and admins: proven promotion tactics plus a complete, organized
        hashtag library ({totalTags} tags) drawn from the MPD tweet bank.
      </p>

      <div className={styles.content}>
        <Link to="/affiliate-admin" className={styles.backLink}>
          &larr; Back to Affiliate Admin
        </Link>

        <section className={styles.section}>
          <h2>Promotion Tips</h2>
          <ul className={styles.tipsList}>
            {suggestions.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Hashtag Library</h2>
          <p className={styles.sectionIntro}>
            Every current hashtag from the tweet bank, grouped by audience with a note on its
            purpose. Use these to target the right people and link to the matching page.
          </p>

          {categories.map(category => (
            <div key={category.name} className={styles.category}>
              <h3 className={styles.categoryName}>{category.name}</h3>
              <p className={styles.categoryDescription}>{category.description}</p>
              <ul className={styles.tagList}>
                {category.tags.map(entry => (
                  <li key={entry.tag} className={styles.tagItem}>
                    <span className={styles.tag}>{entry.tag}</span>
                    <span className={styles.tagPurpose}>{entry.purpose}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
